import { Button } from "@/components/ui/button";
import { ArrowRight, Home, Hammer, Leaf } from "lucide-react";
import { Link } from "react-router-dom";
import CanonicalTag from "@/components/CanonicalTag";

const CraftsmanADUDesignFosterCity = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <CanonicalTag url="https://udaadu.com/craftsman-adu-design-foster-city" />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-6">
            Craftsman ADU Design in Foster City
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Bring timeless charm to Foster City with expertly crafted ADU designs featuring handcrafted details, natural materials, and the warmth of traditional craftsmanship.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="group">
              Get Your Craftsman ADU Quote
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button variant="outline" size="lg">
              View Craftsman Portfolio
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-muted/10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose Craftsman ADU Design in Foster City?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 rounded-lg bg-card border">
              <Home className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Timeless Appeal</h3>
              <p className="text-muted-foreground">
                Classic craftsman elements that never go out of style, perfect for Foster City's family-oriented community atmosphere.
              </p>
            </div>
            <div className="text-center p-6 rounded-lg bg-card border">
              <Hammer className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Quality Craftsmanship</h3>
              <p className="text-muted-foreground">
                Attention to detail in every element, from custom built-ins to handcrafted trim work that showcases superior construction.
              </p>
            </div>
            <div className="text-center p-6 rounded-lg bg-card border">
              <Leaf className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-3">Natural Materials</h3>
              <p className="text-muted-foreground">
                Sustainable wood, stone, and other natural materials that create warmth while respecting Foster City's environmental values.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto prose prose-lg">
          <h2 className="text-3xl font-bold mb-6">Craftsman ADU Design Excellence in Foster City</h2>
          
          <p className="text-lg text-muted-foreground mb-6">
            Foster City's planned community design makes it an ideal location for craftsman-style ADUs that complement the city's emphasis on quality living and environmental consciousness. Our craftsman designs honor traditional building techniques while incorporating modern amenities and efficiency standards.
          </p>

          <h3 className="text-2xl font-semibold mb-4">Signature Craftsman Elements</h3>
          <ul className="space-y-2 text-muted-foreground mb-6">
            <li>• Exposed beam ceilings and natural wood features</li>
            <li>• Custom built-in storage and seating</li>
            <li>• Handcrafted cabinetry and millwork</li>
            <li>• Stone or brick accent features</li>
            <li>• Covered porches and outdoor living spaces</li>
            <li>• Energy-efficient windows with divided lights</li>
          </ul>

          <h3 className="text-2xl font-semibold mb-4">Foster City Craftsman ADU Benefits</h3>
          <p className="text-muted-foreground mb-6">
            Craftsman ADUs in Foster City provide excellent value retention due to their timeless appeal and quality construction. These designs particularly resonate with families and professionals who appreciate the balance of traditional charm and modern functionality in Foster City's unique lagoon community.
          </p>

          <div className="bg-muted/20 p-6 rounded-lg mb-8">
            <h4 className="text-xl font-semibold mb-3">Ready to Build Your Craftsman ADU in Foster City?</h4>
            <p className="text-muted-foreground mb-4">
              Our craftsman specialists bring decades of experience in traditional building techniques combined with modern construction standards.
            </p>
            <Button className="group">
              Start Your Craftsman ADU Project
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-primary/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Enhance Your Foster City Property with Timeless Craftsmanship</h2>
          <p className="text-xl text-muted-foreground mb-8">
            Experience the enduring beauty and quality of craftsman design in your custom Foster City ADU.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="group">
              Get Free Craftsman ADU Consultation
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/contact">Contact Our Design Team</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CraftsmanADUDesignFosterCity;